<template>
    <div class="admin-login-page">
        <!-- 頂部導航 -->
        <div class="top-nav">
            <div class="nav-left">
                <button @click="goBack" class="back-btn">
                    {{ $t('aiprompt.adminLogin.backButton') }}
                </button>
                <h1 class="title">{{ $t('aiprompt.adminLogin.title') }}</h1>
            </div>
        </div>

        <!-- 管理員登入卡片 -->
        <div class="admin-login-container">
            <div class="admin-login-card">
                <div class="admin-header">
                    <div class="admin-icon">🔐</div>
                    <h2>{{ $t('aiprompt.adminLogin.adminVerification') }}</h2>
                    <p class="admin-subtitle">{{ $t('aiprompt.adminLogin.subtitle') }}</p>
                </div>

                <div class="admin-form">
                    <!-- 用戶 ID 顯示 -->
                    <div class="form-group">
                        <label class="form-label">{{ $t('aiprompt.adminLogin.userIdLabel') }}</label>
                        <div class="user-id-display">
                            <span class="user-id">{{ currentUserId }}</span>
                        </div>
                    </div>

                    <!-- 管理員密鑰輸入 -->
                    <div class="form-group">
                        <label class="form-label">{{ $t('aiprompt.adminLogin.passphraseLabel') }}</label>
                        <input 
                            v-model="adminPassphrase"
                            type="password"
                            class="form-input"
                            :placeholder="$t('aiprompt.adminLogin.passphrasePlaceholder')"
                            @keyup.enter="verifyAdmin"
                            @input="clearError"
                            autocomplete="off"
                        />
                        <div v-if="errorMessage" class="error-message">
                            {{ errorMessage }}
                        </div>
                    </div>

                    <!-- 登入按鈕 -->
                    <div class="form-actions">
                        <button 
                            @click="verifyAdmin" 
                            class="verify-btn"
                            :class="{ loading: isLoading }"
                            :disabled="!adminPassphrase || isLoading"
                        >
                            <span v-if="isLoading" class="loading-spinner"></span>
                            {{ isLoading ? $t('aiprompt.adminLogin.verifying') : $t('aiprompt.adminLogin.verifyAndLogin') }}
                        </button>
                        
                        <button @click="goBack" class="cancel-btn">
                            {{ $t('aiprompt.cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Message } from 'view-ui-plus'
import jwt_decode from 'jwt-decode'
import aipromptAPI from '@/api/aipromptAPI'

const router = useRouter()
const { t } = useI18n()

// 響應式狀態
const currentUserId = ref('')
const adminPassphrase = ref('')
const errorMessage = ref('')
const isLoading = ref(false)

// 返回上一頁
const goBack = () => {
    router.go(-1)
}

// 清除錯誤訊息
const clearError = () => {
    errorMessage.value = ''
}

// 驗證管理員權限 (目前只是前端邏輯，後續會接後端)
const verifyAdmin = async () => {
    if (!adminPassphrase.value) {
        errorMessage.value = t('aiprompt.adminLogin.enterPassphrase')
        return
    }

    isLoading.value = true
    errorMessage.value = ''

    try {
        // 呼叫後端 API 進行管理員驗證
        const response = await aipromptAPI.verifyAdminAccess({
            userId: currentUserId.value,
            passphrase: adminPassphrase.value
        })
        
        if (response.success && response.isAdmin) {
            Message.success(t('aiprompt.messages.adminVerificationSuccess'))
            // 存儲管理員狀態
            sessionStorage.setItem('adminVerified', 'true')
            sessionStorage.setItem('adminLoginTime', Date.now().toString())
            
            // 跳轉到管理員上傳頁面
            router.push('/admin-upload')
        } else {
            errorMessage.value = response.message || t('aiprompt.adminLogin.wrongPassphrase')
        }
    } catch (error) {
        console.error('管理員驗證失敗:', error)
        errorMessage.value = t('aiprompt.adminLogin.verificationFailed')
    } finally {
        isLoading.value = false
    }
}

// 初始化：從 token 獲取當前用戶 ID
const initializeUserInfo = () => {
    const idToken = localStorage.getItem('id_token')
    
    if (!idToken) {
        Message.error(t('aiprompt.adminLogin.noLoginInfo'))
        router.push('/ai-prompt-home')
        return
    }

    try {
        const decoded = jwt_decode(idToken)
        currentUserId.value = decoded.sub || '未知'
        
        // 確認是否為管理員用戶
        if (decoded.sub !== '1595321354') {
            Message.error(t('aiprompt.adminLogin.noAdminPermission'))
            router.push('/ai-prompt-home')
        }
    } catch (error) {
        console.error('Token 解析失敗:', error)
        Message.error(t('aiprompt.adminLogin.invalidLoginInfo'))
        router.push('/ai-prompt-home')
    }
}

// 組件掛載時初始化
onMounted(() => {
    initializeUserInfo()
})
</script>

<style lang="less" scoped>
@import "../assets/HTwbCardCSS/HTwbCardVal.less";

.admin-login-page {
    min-height: 100vh;
    background: #f5f5f5;
    
    .top-nav {
        display: flex;
        align-items: center;
        padding: 0 2rem;
        background: white;
        border-bottom: 1px solid #e0e0e0;
        height: @navbar-height;
        
        .nav-left {
            display: flex;
            align-items: center;
            gap: 1rem;
            
            .back-btn {
                background: #f0f0f0;
                border: 1px solid #d0d0d0;
                color: #333;
                padding: 5px;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.3s ease;
                
                &:hover {
                    background: #e0e0e0;
                }
            }
            
            .title {
                color: #333;
                font-size: 1rem;
                font-weight: 600;
                margin: 0;
            }
        }
    }
}

.admin-login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - @navbar-height);
    padding: 2rem;
    
    .admin-login-card {
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        padding: 3rem;
        width: 100%;
        max-width: 450px;
        
        .admin-header {
            text-align: center;
            margin-bottom: 2.5rem;
            
            .admin-icon {
                font-size: 4rem;
                margin-bottom: 1rem;
            }
            
            h2 {
                color: @text-color;
                font-size: 2rem;
                font-weight: 700;
                margin-bottom: 0.5rem;
            }
            
            .admin-subtitle {
                color: @text-color-gray;
                font-size: 1rem;
                margin: 0;
            }
        }
        
        .admin-form {
            .form-group {
                margin-bottom: 1.5rem;
                
                .form-label {
                    display: block;
                    font-weight: 600;
                    color: @text-color;
                    margin-bottom: 0.5rem;
                    font-size: 0.9rem;
                }
                
                .user-id-display {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1rem;
                    background: @bg-color-light;
                    border-radius: 8px;
                    border: 2px solid @border-color-light;
                    
                    .user-id {
                        font-weight: 600;
                        color: @text-color;
                        font-family: 'Courier New', monospace;
                    }
                    
                }
                
                .form-input {
                    width: 100%;
                    padding: 1rem;
                    border: 2px solid @border-color-light;
                    border-radius: 8px;
                    font-size: 1rem;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                    
                    &:focus {
                        outline: none;
                        border-color: @primary-color;
                        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
                    }
                    
                    &::placeholder {
                        color: @text-color-gray;
                    }
                }
                
                .error-message {
                    color: @danger-color;
                    font-size: 0.85rem;
                    margin-top: 0.5rem;
                    font-weight: 500;
                }
            }
            
            .form-actions {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                margin-top: 2rem;
                
                .verify-btn {
                    background: @primary-color;
                    color: white;
                    border: none;
                    padding: 1rem 2rem;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    
                    &:hover:not(:disabled) {
                        background: @primary-color-hover;
                        transform: translateY(-2px);
                        box-shadow: 0 8px 16px rgba(0, 123, 255, 0.3);
                    }
                    
                    &:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                        transform: none;
                        box-shadow: none;
                    }
                    
                    &.loading {
                        cursor: wait;
                    }
                    
                    .loading-spinner {
                        width: 16px;
                        height: 16px;
                        border: 2px solid transparent;
                        border-top: 2px solid currentColor;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                }
                
                .cancel-btn {
                    background: transparent;
                    color: @text-color-gray;
                    border: 2px solid @border-color-light;
                    padding: 1rem 2rem;
                    border-radius: 8px;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    
                    &:hover {
                        border-color: @border-color-gray;
                        color: @text-color;
                    }
                }
            }
        }
        
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

// 響應式設計
@media (max-width: 768px) {
    .admin-login-container {
        padding: 1rem;
        
        .admin-login-card {
            padding: 2rem 1.5rem;
            
            .admin-header {
                margin-bottom: 2rem;
                
                h2 {
                    font-size: 1.5rem;
                }
            }
        }
    }
}
</style>